import { SuccessfulAddConpanyDialog } from '../components/companies/successfulAddCompanyDialog';
import CompaniesPageHeader from '../components/companies/companiesPageHeader';
import { AddCompanyDialog } from '../components/companies/addCompanyDialog';
import CompanyFiltration from '../components/companies/companyFiltration';
import { useState, useCallback, useEffect } from 'react';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { Divider, Box, LinearProgress } from '@mui/material';
import { useHasPermission } from '../hooks/useHasPermission';
import ListView from '../components/companies/ListView';
import GridView from '../components/companies/GridView';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';

const Companies = () => {

    const defaultSortBy = {
        name: null,
        address: null,
    };

    const hasCompaniesCreatePermission = useHasPermission('entreprise_create');

    const [companyId, setCompanyId] = useState(null);
    const [openAddCompany, setOpenAddCompany] = useState(false);
    const [openSuccessfulAddCompany, setOpenSuccessfulAddCompany] = useState(false);

    const [loading, setLoading] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [totalCompanies, setTotalCompanies] = useState('');
    const [companiesFetched, setCompaniesFetched] = useState(0);
    const [filteredCompaniesCount, setFilteredCompaniesCount] = useState(0);
    const [selectedDisplayMode, setSelectedDisplayMode] = useState(JSON.parse(localStorage.getItem('selectedCompaniesDisplayMode')) || 'grid');
    const [sortBy, setSortBy] = useState(() => {
        const storedSortBy = JSON.parse(localStorage.getItem('companySortBy')) || {};
        return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
    });
     const [companyFilters, setCompanyFilters] = useState(JSON.parse(localStorage.getItem('companyFilters')) || { debouncedSearch: '' });

    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'companies',
        pathname: '/dashboard/entreprises',
        displayMode: selectedDisplayMode,
        itemSizes: { grid: 280, list: 70 },
        resetOnFilters: companyFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(companyFilters);

    useEffect(() => {
        setLoading(true)
    }, [companyFilters]);

    useEffect(() => {
        localStorage.setItem('selectedCompaniesDisplayMode', JSON.stringify(selectedDisplayMode));
    }, [selectedDisplayMode]);

    useEffect(() => {
        localStorage.setItem('companyFilters', JSON.stringify(companyFilters));
    }, [companyFilters]);

    useEffect(() => {
        localStorage.setItem('companySortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    const fetchCompanies = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('company', { params: { offset: adjustedOffset, limit, filters: companyFilters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedCompanies = [];
                // Update the fetched records in their correct positions
                response?.data?.companiesData?.forEach((company, index) => updatedCompanies[adjustedOffset + index] = company);
                // Update the state with the new files array
                setCompanies(updatedCompanies);
                setFilteredCompaniesCount(response?.data?.filteredCompaniesCount);
                setTotalCompanies(response?.data?.totalCompaniesCount);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching companies:', error);
            CustomErrorLogger(error, 'error fetch companies in companies page');
        }
    }, [companyFilters, offset, sortBy]);

    useEffect(() => {
        fetchCompanies()
    }, [fetchCompanies]);


    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Entreprise" list={true}>

            <CompaniesPageHeader setOpenAddCompany={setOpenAddCompany} />
            <Divider />
            {loading ?
                <Box>
                    <CompanyFiltration
                        filtersEmpty={filtersEmpty}
                        companyFilters={companyFilters}
                        setCompanyFilters={setCompanyFilters}
                        totalCompanies={totalCompanies}
                        filteredCompaniesCount={filteredCompaniesCount}
                        companiesFetched={companiesFetched}
                        selectedDisplayMode={selectedDisplayMode}
                        setSelectedDisplayMode={setSelectedDisplayMode}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                totalCompanies === 0 ?
                    <NoRecord
                        description="Aucun entreprise ajouté pour le moment"
                        buttonTitle="Ajouter une entreprise"
                        handleOpenDialog={() => setOpenAddCompany(true)}
                        hasPermission={hasCompaniesCreatePermission}
                    /> :
                    <Box>
                        <CompanyFiltration
                            filtersEmpty={filtersEmpty}
                            companyFilters={companyFilters}
                            setCompanyFilters={setCompanyFilters}
                            totalCompanies={totalCompanies}
                            filteredCompaniesCount={filteredCompaniesCount}
                            companiesFetched={companiesFetched}
                            selectedDisplayMode={selectedDisplayMode}
                            setSelectedDisplayMode={setSelectedDisplayMode}
                        />

                        {selectedDisplayMode === 'grid' ? (
                            <GridView
                                scrollRef={scrollRef}
                                companies={companies}
                                setOffset={setOffset}
                                sortBy={sortBy}
                                filteredCompaniesCount={filteredCompaniesCount}
                                setCompaniesFetched={setCompaniesFetched}
                                setSortBy={setSortBy}
                                companyFilters={companyFilters}
                            />
                        ) : (
                            <ListView
                                scrollRef={scrollRef}
                                companies={companies}
                                setOffset={setOffset}
                                sortBy={sortBy}
                                filteredCompaniesCount={filteredCompaniesCount}
                                setCompaniesFetched={setCompaniesFetched}
                                setSortBy={setSortBy}
                            />
                        )}

                    </Box>
            }

            {openAddCompany &&
                <AddCompanyDialog
                    setCompanies={setCompanies}
                    setTotalCompanies={setTotalCompanies}
                    setFilteredCompaniesCount={setFilteredCompaniesCount}
                    openAddCompany={openAddCompany}
                    setOpenAddCompany={setOpenAddCompany}
                    setCompanyId={setCompanyId}
                />
            }

            {openSuccessfulAddCompany &&
                <SuccessfulAddConpanyDialog
                    openSuccessfulAddCompany={openSuccessfulAddCompany}
                    companyId={companyId}
                    setOpenAddCompany={setOpenAddCompany}
                />
            }

        </Page>
        </ListNavigationProvider>
    );
};

export default Companies;